/* eslint-disable max-lines */
/* eslint-disable max-len */

import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Days from "../../../components/lines-managements/trips/Days";
import EnhancedDayPicker, { SelectedDateType } from "../../../components/lines-managements/trips/EnhancedDayPicker";
import CreateTripWrapper from "../../../components/lines-managements/trips/schedules/CreateTripWrapper";
import Box from "../../../components/ui/Box";
import Button from "../../../components/ui/Button";
import SpinnerLoader from "../../../components/ui/SpinnerLoader";
import SelectInput from "../../../components/ui/input/SelectInput";
import TimePartsField, { HOUR_OPTIONS_DURATION, pad2 } from "../../../components/ui/input/TimePartsField";
import { translateServerMessage, withServerReason } from "../../../utils/apiErrorMessage";

// "2 orare u përditësuan. 1 dështoi (ID 20097). <reason>": the ids and the server's reason, so the
// operator knows what to fix instead of reading a bare count inside a success toast.
const bulkFailureMessage = (result: {
  updatedTripIds: number[];
  failedTripIds: number[];
  message?: string;
}): string => {
  const failed = result.failedTripIds.length;
  const reason = result.message ? ` ${translateServerMessage(result.message)}` : "";
  return `${result.updatedTripIds.length} orare u përditësuan. ${failed} ${
    failed === 1 ? "dështoi" : "dështuan"
  } (ID ${result.failedTripIds.join(", ")}).${reason}`;
};
import useTimeList from "../../../components/ui/input/TimePicker";
import BulkAffectedReservationsModal from "../../../components/lines-managements/trips/BulkAffectedReservationsModal";
import { BulkUpdateLocationState, IBulkNotifyTripEntry, IBulkUpdatedTripAffected, ITripTimeUpdate, TripPreviewRow } from "../../../interfaces/endpoints/trips/IBulkEditTripsWorkingDays";
import { CustomTripWorkingDayType, IOperationalInterval } from "../../../interfaces/endpoints/trips/Trips.types";
import { bulkUpdateTripsPrices } from "../../../services/Pricing.service";
import { bulkEditTripsWorkingDays, bulkUpdateTripsTimes, notifyBulkTripsChangeEmails } from "../../../services/Trips.service";
import "../../../styles/shared-calendar.css";
import { MAX_TRIP_DURATION_MINUTES, collidingTripIds, formatArrivalPreview, formatTripLength, tripTimesLabel } from "../../../utils/tripDuration";

type BulkUpdateMode = "workingDays" | "prices" | "times";

// How a submit ended, which decides whether the page may leave. "refused": a field was invalid or the
// server failed some trips, so the operator stays with every edit in place to fix it and resubmit.
// "modal": the affected-passengers dialog opens and its own close/confirm navigates. "done": leave.
type SubmitOutcome = "refused" | "modal" | "done";

const MODE_OPTIONS: { value: BulkUpdateMode; label: string }[] = [
  { value: "workingDays", label: "Ditët e udhëtimit" },
  { value: "prices", label: "Çmimet" },
  { value: "times", label: "Oraret" }
];

// Render operational interval like in Trips list
const renderOperationalInterval = (row: TripPreviewRow): React.ReactNode => {
  if (row.hasCustomDates && row.customWorkingDays && row.customWorkingDays.length > 0) {
    const exceptionDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.ExceptionDate
    );
    const workingDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.SelectedWorkingDate
    );

    return (
      <div className="space-y-1">
        <span className="text-xs text-gray-600 italic block">
          Ka data specifike të punës
        </span>
        <div className="flex flex-wrap gap-1">
          {exceptionDates.length > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                font-medium bg-[#fef7f5] text-[#f05a2d]"
            >
              Përjashtimet: {format(new Date(exceptionDates[0].date), "dd/MM/yyyy")}
              {exceptionDates.length > 1 && (
                <span className="ml-1 text-gray-500">
                  +{exceptionDates.length - 1}
                </span>
              )}
            </span>
          )}
          {workingDates.length > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                font-medium bg-[#fef7f5] text-[#f05a2d]"
            >
              Datat e punës: {format(new Date(workingDates[0].date), "dd/MM/yyyy")}
              {workingDates.length > 1 && (
                <span className="ml-1 text-gray-500">
                  +{workingDates.length - 1}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <Days
      days={[
        row.operationalInterval.monday,
        row.operationalInterval.tuesday,
        row.operationalInterval.wednesday,
        row.operationalInterval.thursday,
        row.operationalInterval.friday,
        row.operationalInterval.saturday,
        row.operationalInterval.sunday
      ]}
    />
  );
};

// Convert "HH:mm:ss" or "HH:mm" departure/arrival string to "HH:mm"
const toTimeInputValue = (timeStr: string): string => {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
  }
  return timeStr;
};

// Parse "HH:mm" to total minutes
const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

// Convert total minutes to "HH:mm" (wraps at 24h)
const minutesToTime = (mins: number): string => {
  const wrapped = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const BulkUpdateTrips = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as BulkUpdateLocationState | null;

  // Bulk update mode
  const [bulkUpdateMode, setBulkUpdateMode] = useState<BulkUpdateMode>("workingDays");

  // Track selected trip IDs (allows deselection)
  const [selectedTripIds, setSelectedTripIds] = useState<Set<number>>(
    () => new Set(state?.tripIds ?? [])
  );

  // Working days type (ExceptionDate vs. SelectedWorkingDate)
  const [workingDaysType, setWorkingDaysType] = useState<CustomTripWorkingDayType>(
    CustomTripWorkingDayType.ExceptionDate
  );

  // Operational interval (weekly days)
  const [operationalInterval, setOperationalInterval] = useState<IOperationalInterval>({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true
  });

  // Custom dates selection
  const [selectedDatesCustom, setSelectedDatesCustom] = useState<SelectedDateType[]>([]);

  // Price update state
  const [ageGroupId, setAgeGroupId] = useState<number>(1);
  const [oneWayPrice, setOneWayPrice] = useState<string>("");
  const [roundTripPrice, setRoundTripPrice] = useState<string>("");

  // The length a trip starts the edit with. A trip stored with no length (0 - the scraper leaves
  // those when an operator's site only shows clocks) starts from its own clock pair instead, so
  // moving its departure can never save it as a zero-minute trip. Null when even the clocks say
  // nothing (equal or missing): the operator then has to fill the length before saving.
  const startingLength = (row: TripPreviewRow): number | null => {
    if (row.durationMinutes > 0) return row.durationMinutes;
    const dep = timeToMinutes(toTimeInputValue(row.departureTimeString));
    const arr = timeToMinutes(toTimeInputValue(row.arrivalTimeString));
    if (isNaN(dep) || isNaN(arr)) return null;
    const diff = arr - dep;
    const length = diff < 0 ? diff + 1440 : diff;
    return length > 0 ? length : null;
  };

  // Per trip: the departure "HH:mm" and the length in minutes. The arrival is not stored - it is
  // always departure + length and is shown read-only, so the two can never disagree.
  type RowTimes = { departureTime: string; durationMinutes: number | null };
  const [tripTimes, setTripTimes] = useState<Map<number, RowTimes>>(() => {
    const map = new Map<number, RowTimes>();
    state?.tripPreviewRows?.forEach((row) => {
      map.set(row.tripId, {
        departureTime: toTimeInputValue(row.departureTimeString),
        durationMinutes: startingLength(row)
      });
    });
    return map;
  });
  // Which row has its departure list open; one at a time.
  const [openDepartureFor, setOpenDepartureFor] = useState<number | null>(null);
  // One entry per minute of the day, the scrolling list operators know from the trip form.
  const timeOptions = useTimeList();

  // The selected rows that share a clock pair with another selected row. Only those
  // labels need the trip id, which is the one value guaranteed to differ.
  const collidingIds = useMemo(
    () =>
      collidingTripIds(
        (state?.tripPreviewRows ?? []).map((row) => ({
          id: row.tripId,
          departureTimeString: row.departureTimeString,
          arrivalTimeString: row.arrivalTimeString
        }))
      ),
    [state]
  );

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bulk affected reservations modal state
  const [showBulkAffectedModal, setShowBulkAffectedModal] = useState(false);
  const [bulkAffected, setBulkAffected] = useState<IBulkUpdatedTripAffected[]>([]);

  const tripLabelById = useMemo<Record<number, string>>(() => {
    if (!state?.tripPreviewRows) return {};
    return state.tripPreviewRows.reduce<Record<number, string>>((acc, row) => {
      acc[row.tripId] = row.routeName;
      return acc;
    }, {});
  }, [state?.tripPreviewRows]);

  // Handle trip selection toggle
  const handleTripToggle = useCallback((tripId: number) => {
    setSelectedTripIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  }, []);

  // Handle select/deselect all trips
  const handleToggleAll = useCallback(() => {
    if (!state?.tripIds) return;
    setSelectedTripIds((prev) => {
      if (prev.size === state.tripIds.length) {
        return new Set();
      }
      return new Set(state.tripIds);
    });
  }, [state?.tripIds]);

  // Handle operational interval checkbox change
  const handleDayChange = useCallback((day: keyof IOperationalInterval) => {
    setOperationalInterval((prev) => ({
      ...prev,
      [day]: !prev[day]
    }));
  }, []);

  // Handle date selection change
  const handleDateSelectionChange = useCallback((dates: SelectedDateType[]) => {
    setSelectedDatesCustom(dates);
  }, []);

  // Handle working days type change
  const handleWorkingDaysTypeChange = useCallback((newType: CustomTripWorkingDayType) => {
    setWorkingDaysType(newType);
    setSelectedDatesCustom([]);
  }, []);

  const setDeparture = useCallback((tripId: number, value: string) => {
    setTripTimes((prev) => {
      const next = new Map(prev);
      const current = next.get(tripId) ?? { departureTime: "", durationMinutes: null };
      next.set(tripId, { ...current, departureTime: value });
      return next;
    });
    setOpenDepartureFor(null);
  }, []);

  const setDurationPart = useCallback((tripId: number, part: "hours" | "minutes", value: string) => {
    setTripTimes((prev) => {
      const next = new Map(prev);
      const current = next.get(tripId) ?? { departureTime: "", durationMinutes: null };
      const total = current.durationMinutes ?? 0;
      const hours = part === "hours" ? Number(value) : Math.floor(total / 60);
      const minutes = part === "minutes" ? Number(value) : total % 60;
      next.set(tripId, {
        ...current,
        durationMinutes: Math.min(hours * 60 + minutes, MAX_TRIP_DURATION_MINUTES)
      });
      return next;
    });
  }, []);

  // LEGACY (replaced 2026-09-21 by the two setters above; the arrival is derived now):
  /*
  const handleTripTimeChange = useCallback((tripId: number, field: "departureTime" | "arrivalTime", value: string) => {
    const duration = tripDurations.get(tripId) || 0;
    const isComplete = /^\d{2}:\d{2}$/.test(value);
    setTripTimes((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(tripId) || { departureTime: "", arrivalTime: "" };
      if (isComplete && field === "departureTime") {
        const depMinutes = timeToMinutes(value);
        if (duration >= 1440) {
          // Multi-day trip (>= 24h): the HH:mm arrival can't encode the real
          // arrival day, so recomputing it as (departure + duration) % 24h
          // would silently re-anchor the trip to under a day. Shift the shown
          // arrival by the same signed delta as the departure instead.
          const canShift = /^\d{2}:\d{2}$/.test(current.departureTime) &&
            /^\d{2}:\d{2}$/.test(current.arrivalTime);
          if (canShift) {
            const delta = depMinutes - timeToMinutes(current.departureTime);
            newMap.set(tripId, {
              departureTime: value,
              arrivalTime: minutesToTime(timeToMinutes(current.arrivalTime) + delta)
            });
          } else {
            // The shift baseline was lost (e.g. the departure was cleared mid-edit).
            // Recompute the arrival clock from the stored length - it's exactly what
            // the server will derive on save, and a blank here would fail the submit
            // validation for the whole batch with no way to type a value back in.
            newMap.set(tripId, {
              departureTime: value,
              arrivalTime: minutesToTime(depMinutes + duration)
            });
          }
        } else {
          newMap.set(tripId, {
            departureTime: value,
            arrivalTime: minutesToTime(depMinutes + duration)
          });
        }
      } else {
        newMap.set(tripId, { ...current, [field]: value });
      }
      return newMap;
    });
  }, [tripDurations]);
  */

  // Submit bulk update for working days.
  const handleSubmitWorkingDays = useCallback(async (): Promise<SubmitOutcome> => {
    const customWorkingDays = selectedDatesCustom.map((d) => ({
      type: workingDaysType,
      date: new Date(Date.UTC(
        d.date.getFullYear(),
        d.date.getMonth(),
        d.date.getDate(),
        12, 0, 0, 0
      ))
    }));

    const tripIdsArray = Array.from(selectedTripIds);

    const response = await bulkEditTripsWorkingDays({
      tripIds: tripIdsArray,
      operationalInterval,
      customWorkingDays
    });

    const result = response.data;
    if (result.failedTripIds && result.failedTripIds.length > 0) {
      toast.error(bulkFailureMessage(result), { position: "top-right", duration: 8000 });
    } else {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan me sukses.`,
        { position: "top-right" }
      );
    }

    const affectedWithPassengers = (result.affectedByTrip ?? [])
      .filter((x) => x.affected?.affectedPassengers?.length > 0);

    if (affectedWithPassengers.length > 0) {
      setBulkAffected(affectedWithPassengers);
      setShowBulkAffectedModal(true);
      return "modal";
    }

    return result.failedTripIds?.length ? "refused" : "done";
  }, [selectedTripIds, operationalInterval, selectedDatesCustom, workingDaysType]);

  // Submit bulk update for prices
  const handleSubmitPrices = useCallback(async (): Promise<SubmitOutcome> => {
    const parsedOneWay = parseFloat(oneWayPrice);
    const parsedRoundTrip = parseFloat(roundTripPrice);

    if (isNaN(parsedOneWay) || parsedOneWay < 0) {
      toast.error("Ju lutem vendosni një çmim valid për një drejtim.");
      return "refused";
    }
    if (isNaN(parsedRoundTrip) || parsedRoundTrip < 0) {
      toast.error("Ju lutem vendosni një çmim valid për vajtje-ardhje.");
      return "refused";
    }

    const tripIdsArray = Array.from(selectedTripIds);

    const response = await bulkUpdateTripsPrices({
      tripIds: tripIdsArray,
      ageGroupId,
      oneWayPrice: parsedOneWay,
      roundTripPrice: parsedRoundTrip
    });

    const result = response.data;
    if (result.failedTripIds && result.failedTripIds.length > 0) {
      toast.error(bulkFailureMessage(result), { position: "top-right", duration: 8000 });
    } else {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan me sukses.`,
        { position: "top-right" }
      );
    }
    return result.failedTripIds?.length ? "refused" : "done";
  }, [selectedTripIds, ageGroupId, oneWayPrice, roundTripPrice]);

  // Submit bulk update for times.
  const handleSubmitTimes = useCallback(async (): Promise<SubmitOutcome> => {
    const tripIdsArray = Array.from(selectedTripIds);
    const trips: ITripTimeUpdate[] = [];
    for (let i = 0; i < tripIdsArray.length; i++) {
      const tripId = tripIdsArray[i];
      const times = tripTimes.get(tripId);
      if (!times || !/^\d{2}:\d{2}$/.test(times.departureTime)) {
        toast.error("Ju lutem zgjidhni orën e nisjes për të gjitha oraret e zgjedhura.");
        return "refused";
      }
      const length = times.durationMinutes;
      if (!length || length < 1 || length > MAX_TRIP_DURATION_MINUTES) {
        toast.error("Ju lutem plotësoni kohëzgjatjen për të gjitha oraret e zgjedhura.");
        return "refused";
      }
      // The arrival clock is sent for older readers of this request; the server derives it from
      // the length anyway, so the two are always the same number.
      const arrival = minutesToTime(timeToMinutes(times.departureTime) + length);
      trips.push({
        tripId,
        departureTime: `${times.departureTime}:00`,
        arrivalTime: `${arrival}:00`,
        durationMinutes: length
      });
    }

    const response = await bulkUpdateTripsTimes({ trips });

    const result = response.data;
    if (result.failedTripIds && result.failedTripIds.length > 0) {
      toast.error(bulkFailureMessage(result), { position: "top-right", duration: 8000 });
    } else {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan me sukses.`,
        { position: "top-right" }
      );
    }

    const affectedWithPassengers = (result.affectedByTrip ?? [])
      .filter((x) => x.affected?.affectedPassengers?.length > 0);

    if (affectedWithPassengers.length > 0) {
      setBulkAffected(affectedWithPassengers);
      setShowBulkAffectedModal(true);
      return "modal";
    }

    return result.failedTripIds?.length ? "refused" : "done";
  }, [selectedTripIds, tripTimes]);

  // Submit handler
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedTripIds.size === 0) {
      toast.error("Nuk ka orare të zgjedhura për përditësim.");
      return;
    }

    setIsSubmitting(true);

    try {
      let outcome: SubmitOutcome;
      if (bulkUpdateMode === "workingDays") {
        outcome = await handleSubmitWorkingDays();
      } else if (bulkUpdateMode === "prices") {
        outcome = await handleSubmitPrices();
      } else {
        outcome = await handleSubmitTimes();
      }

      if (outcome === "done") {
        setTimeout(() => {
          navigate("/trips", { state: ["Menaxhimi i linjave", "Oraret"] });
        }, 500);
      }
    } catch (error) {
      console.error("Bulk update failed:", error);
      toast.error(withServerReason("Ndodhi një gabim gjatë përditësimit të orareve.", error), {
        position: "top-right",
        duration: 8000
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTripIds, bulkUpdateMode, handleSubmitWorkingDays, handleSubmitPrices, handleSubmitTimes, navigate]);

  const handleBulkAffectedModalClose = useCallback(() => {
    setShowBulkAffectedModal(false);
    setBulkAffected([]);
    setTimeout(() => {
      navigate("/trips", { state: ["Menaxhimi i linjave", "Oraret"] });
    }, 300);
  }, [navigate]);

  const handleBulkAffectedConfirm = useCallback(async (selection: IBulkNotifyTripEntry[]) => {
    try {
      const response = await notifyBulkTripsChangeEmails({ trips: selection });
      if (response.data) {
        toast.success("Njoftimet me email u dërguan me sukses.", { position: "top-right" });
      } else {
        toast.error("Ndodhi një gabim gjatë dërgimit të njoftimeve me email.", { position: "top-right" });
      }
    } catch (error) {
      toast.error("Ndodhi një gabim gjatë dërgimit të njoftimeve me email.", { position: "top-right" });
    }

    setShowBulkAffectedModal(false);
    setBulkAffected([]);
    setTimeout(() => {
      navigate("/trips", { state: ["Menaxhimi i linjave", "Oraret"] });
    }, 500);
  }, [navigate]);

  // If no state is passed, show empty state
  if (!state?.tripIds || state.tripIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Nuk ka orare të zgjedhura
        </h2>
        <p className="text-gray-500 mb-6">
          Ju lutem zgjidhni oraret që dëshironi të përditësoni nga faqja e orareve.
        </p>
        <Link
          to="/trips"
          className="flex items-center text-primary hover:underline"
          state={["Menaxhimi i linjave", "Oraret"]}
        >
          <ChevronLeftIcon className="w-4 h-4 mr-1" />
          Kthehu tek oraret
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/trips"
        state={["Menaxhimi i linjave", "Oraret"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek oraret</span>
      </Link>

      <Box className="p-0">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <h1 className="text-xl mb-2 py-3 px-5 border-b">
            Përditëso {selectedTripIds.size} orare
          </h1>

          <div className="p-5 overflow-auto">
            {/* Selected trips preview */}
            <CreateTripWrapper title="Oraret e zgjedhura" className="border-none mb-6">
              <div className="max-h-72 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="py-2 px-3 w-10">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={selectedTripIds.size === state.tripIds.length}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = selectedTripIds.size > 0 &&
                              selectedTripIds.size < state.tripIds.length;
                            }
                          }}
                          onChange={handleToggleAll}
                          title={selectedTripIds.size === state.tripIds.length ?
                             "Çzgjidh të gjitha" : "Zgjidh të gjitha"}
                        />
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Linja</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Orari</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Ditët e udhëtimit</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Një drejtim</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Vajtje-ardhje</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {state.tripPreviewRows.map((row) => (
                      <tr
                        key={row.tripId}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          !selectedTripIds.has(row.tripId) ? "opacity-50" : ""
                        }`}
                        onClick={() => handleTripToggle(row.tripId)}
                      >
                        <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="cursor-pointer"
                            checked={selectedTripIds.has(row.tripId)}
                            onChange={() => handleTripToggle(row.tripId)}
                          />
                        </td>
                        <td className="py-2 px-3">{row.routeName}</td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          {tripTimesLabel(
                            row.departureTimeString,
                            row.arrivalTimeString,
                            row.durationMinutes,
                            row.tripId,
                            collidingIds.has(row.tripId)
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {renderOperationalInterval(row)}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {typeof row.prices?.[0] === "number" ? `${row.prices[0].toFixed(2)}€` : "-"}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {typeof row.prices?.[1] === "number" ? `${row.prices[1].toFixed(2)}€` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CreateTripWrapper>

            {/* Mode selector */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden w-fit mb-5">
              {MODE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setBulkUpdateMode(option.value)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    bulkUpdateMode === option.value
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } ${option.value !== "workingDays" ? "border-l border-gray-300" : ""}`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Working days selection */}
            {bulkUpdateMode === "workingDays" && (
              <CreateTripWrapper title="Ditët e udhëtimit" className="border-none mt-5">
                <div className="space-y-4">
                  <div className="flex gap-6 md:px-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="workingDaysType"
                        checked={workingDaysType === CustomTripWorkingDayType.ExceptionDate}
                        onChange={() =>
                          handleWorkingDaysTypeChange(CustomTripWorkingDayType.ExceptionDate)
                        }
                        className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-2 text-gray-700">Përsëritje javore me përjashtime</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="workingDaysType"
                        checked={workingDaysType === CustomTripWorkingDayType.SelectedWorkingDate}
                        onChange={() =>
                          handleWorkingDaysTypeChange(CustomTripWorkingDayType.SelectedWorkingDate)
                        }
                        className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-2 text-gray-700">Datat specifike të punës</span>
                    </label>
                  </div>

                  <div>
                    {workingDaysType === CustomTripWorkingDayType.ExceptionDate && (
                      <div className="flex-wrap flex gap-7 md:gap-0">
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="monday"
                            className="cursor-pointer"
                            checked={operationalInterval.monday}
                            onChange={() => handleDayChange("monday")}
                          />
                          <label
                            htmlFor="monday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Hënë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="tuesday"
                            className="cursor-pointer"
                            checked={operationalInterval.tuesday}
                            onChange={() => handleDayChange("tuesday")}
                          />
                          <label
                            htmlFor="tuesday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Martë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="wednesday"
                            className="cursor-pointer"
                            checked={operationalInterval.wednesday}
                            onChange={() => handleDayChange("wednesday")}
                          />
                          <label
                            htmlFor="wednesday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Mërkurë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="thursday"
                            className="cursor-pointer"
                            checked={operationalInterval.thursday}
                            onChange={() => handleDayChange("thursday")}
                          />
                          <label
                            htmlFor="thursday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Enjte
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="friday"
                            className="cursor-pointer"
                            checked={operationalInterval.friday}
                            onChange={() => handleDayChange("friday")}
                          />
                          <label
                            htmlFor="friday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Premte
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="saturday"
                            className="cursor-pointer"
                            checked={operationalInterval.saturday}
                            onChange={() => handleDayChange("saturday")}
                          />
                          <label
                            htmlFor="saturday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Shtunë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="sunday"
                            className="cursor-pointer"
                            checked={operationalInterval.sunday}
                            onChange={() => handleDayChange("sunday")}
                          />
                          <label
                            htmlFor="sunday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Diel
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="w-full flex justify-center mt-4">
                      <div className="w-[350px]">
                        <EnhancedDayPicker
                          selectedDates={selectedDatesCustom}
                          onChange={handleDateSelectionChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CreateTripWrapper>
            )}

            {/* Prices update */}
            {bulkUpdateMode === "prices" && (
              <CreateTripWrapper title="Përditëso çmimet" className="border-none mt-5">
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grupi i moshës
                    </label>
                    <select
                      value={ageGroupId}
                      onChange={(e) => setAgeGroupId(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                        focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    >
                      <option value={1}>I/e rritur</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="oneWayPrice" className="block text-sm font-medium text-gray-700 mb-1">
                      Çmimi një drejtim (€)
                    </label>
                    <input
                      id="oneWayPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={oneWayPrice}
                      onChange={(e) => setOneWayPrice(e.target.value)}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                        focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  <div>
                    <label htmlFor="roundTripPrice" className="block text-sm font-medium text-gray-700 mb-1">
                      Çmimi vajtje-ardhje (€)
                    </label>
                    <input
                      id="roundTripPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={roundTripPrice}
                      onChange={(e) => setRoundTripPrice(e.target.value)}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                        focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    Çmimet e njëjta do të aplikohen për të gjitha oraret e zgjedhura dhe të gjitha kombinacionet e stacioneve.
                  </p>
                </div>
              </CreateTripWrapper>
            )}

            {/* Times update */}
            {bulkUpdateMode === "times" && (
              <CreateTripWrapper title="Përditëso oraret e nisjes dhe mbërritjes" className="border-none mt-5">
                <div className="border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Linja</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Orari aktual</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Nisja e re</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Kohëzgjatja</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Mbërritja e re</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {state.tripPreviewRows
                        .filter((row) => selectedTripIds.has(row.tripId))
                        .map((row) => {
                          const times = tripTimes.get(row.tripId) ?? { departureTime: "", durationMinutes: null };
                          const length = times.durationMinutes;
                          const isMultiDay = row.durationMinutes >= 1440;
                          return (
                            <tr key={row.tripId} className="hover:bg-gray-50">
                              <td className="py-2 px-3">{row.routeName}</td>
                              <td className="py-2 px-3 whitespace-nowrap text-gray-500">
                                {row.departureTimeString} - {row.arrivalTimeString}
                                {isMultiDay && (
                                  <span className="ml-1 text-xs text-gray-400">{`(${formatTripLength(row.durationMinutes)})`}</span>
                                )}
                              </td>
                              <td className="py-2 px-3 min-w-[9rem]" onClick={(e) => e.stopPropagation()}>
                                <SelectInput
                                  label=""
                                  placeholder="Zgjedh"
                                  options={timeOptions}
                                  selectedId={times.departureTime}
                                  isOpen={openDepartureFor === row.tripId}
                                  onClick={() => setOpenDepartureFor((cur) => (cur === row.tripId ? null : row.tripId))}
                                  onOptionSelect={(option) => setDeparture(row.tripId, option ? String(option.id) : "")}
                                  className="w-full"
                                />
                              </td>
                              <td className="py-2 px-3 min-w-[8rem]" onClick={(e) => e.stopPropagation()}>
                                <TimePartsField
                                  id={`bulkDuration-${row.tripId}`}
                                  label=""
                                  hourOptions={HOUR_OPTIONS_DURATION}
                                  hours={length !== null ? pad2(Math.floor(length / 60)) : ""}
                                  minutes={length !== null ? pad2(length % 60) : ""}
                                  onPartChange={(part, value) => setDurationPart(row.tripId, part, value)}
                                />
                              </td>
                              <td className="py-2 px-3 whitespace-nowrap">
                                {/* Computed, deliberately not an input: departure + length, with the day it
                                    lands on. Plain text so it reads as a result, not a field to fill. */}
                                <span
                                  className="text-sm font-semibold text-gray-500 select-none"
                                  title="Llogaritet nga nisja dhe kohëzgjatja"
                                  aria-live="polite"
                                >
                                  {formatArrivalPreview(times.departureTime, length ?? undefined) || (
                                    <span className="font-normal text-gray-400">--:--</span>
                                  )}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Zgjidh nisjen dhe kohëzgjatjen; mbërritja llogaritet vetë. Kohët përditësohen vetëm për oraret e zgjedhura.
                </p>
              </CreateTripWrapper>
            )}
          </div>

          {/* Footer with submit button */}
          <div className="border-t p-4 justify-end flex sticky bottom-0 rounded-b-lg bg-white z-10">
            <div>
              <Button
                btnType="custom"
                className="border border-primary text-primary mr-4"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                Anulo
              </Button>
              <Button type="submit" disabled={isSubmitting || selectedTripIds.size === 0}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <SpinnerLoader />
                    Duke përditësuar...
                  </span>
                ) : (
                  "Ruaj ndryshimet"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Box>

      <BulkAffectedReservationsModal
        isOpen={showBulkAffectedModal}
        onClose={handleBulkAffectedModalClose}
        onConfirm={handleBulkAffectedConfirm}
        affectedByTrip={bulkAffected}
        tripLabelById={tripLabelById}
      />
    </div>
  );
};

export default BulkUpdateTrips;
